import { useCallback, useEffect, useState } from "react";
import Button from "../Common/Button";
import ConfirmModal from "../Common/ConfirmModal";
import Input from "../Common/Input";
import Label from "../Common/Label";
import LoadingSpinner from "../Common/LoadingSpinner";
import Pagination from "../Common/Pagination";
import AccessTypeDropdown, { accessTypeLabels } from "./AccessTypeDropdown";
import { fieldClassName } from "./formStyles";
import { isValidEmail } from "../../utils/common_utils";
import { trackEvent } from "../../lib/analytics";
import {
  AnalyticsSchemas,
  UserManagementAction,
  UserManagementScope,
} from "../../lib/analyticsEvents";
import {
  createFMSCustomerUser,
  deleteFMSCustomerUser,
  getFMSCustomerUsers,
} from "../../lib/api";

const PAGE_SIZE = 10;

const emptyUserForm = {
  email: "",
  firstName: "",
  lastName: "",
  password: "",
  userAccessType: "FULL_ACCESS",
};

const isSameEmail = (left = "", right = "") =>
  Boolean(left) && left.trim().toLowerCase() === right.trim().toLowerCase();

const getUserDisplayName = (user) => {
  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim();
  return fullName || "--";
};

const CustomerUsersPanel = ({ customerUnid, primaryContact, onNotify }) => {
  const [users, setUsers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [isDeletingUser, setIsDeletingUser] = useState(false);
  const [userForm, setUserForm] = useState(emptyUserForm);
  const [userToDelete, setUserToDelete] = useState(null);

  const pageCount = Math.ceil(totalCount / PAGE_SIZE);

  const loadUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getFMSCustomerUsers(customerUnid, {
        pageNumber: currentPage + 1,
        pageSize: PAGE_SIZE,
      });
      const payload = response?.data?.data || {};
      setUsers(payload.users || []);
      setTotalCount(payload.count || 0);
    } catch (error) {
      setUsers([]);
      setTotalCount(0);
      onNotify(error.message || "Failed to fetch customer users.", "failed");
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, customerUnid, onNotify]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const closeAddModal = () => {
    if (isCreatingUser) return;
    setIsAddOpen(false);
    setUserForm(emptyUserForm);
  };

  const handleCreateUser = async () => {
    const payload = {
      email: userForm.email.trim(),
      firstName: userForm.firstName.trim(),
      lastName: userForm.lastName.trim(),
      userAccessType: userForm.userAccessType,
    };

    if (userForm.password.trim()) {
      payload.password = userForm.password;
    }

    setIsCreatingUser(true);
    try {
      await createFMSCustomerUser(customerUnid, payload);
      trackEvent(AnalyticsSchemas.webUserManagement, {
        action: UserManagementAction.create,
        scope: UserManagementScope.gradientAdmin,
        target_user_unid: null,
      });
      setIsAddOpen(false);
      setUserForm(emptyUserForm);
      onNotify("User created successfully.", "success");
      if (currentPage === 0) {
        loadUsers();
      } else {
        setCurrentPage(0);
      }
    } catch (error) {
      onNotify(error.message || "Failed to create user.", "failed");
    } finally {
      setIsCreatingUser(false);
    }
  };

  const handleDeleteUserConfirmed = async () => {
    if (!userToDelete?.unid) return;

    setIsDeletingUser(true);
    try {
      await deleteFMSCustomerUser(customerUnid, userToDelete.unid);
      trackEvent(AnalyticsSchemas.webUserManagement, {
        action: UserManagementAction.delete,
        scope: UserManagementScope.gradientAdmin,
        target_user_unid: userToDelete.unid,
      });
      setUserToDelete(null);
      onNotify("User deleted successfully.", "success");

      const nextPageCount = Math.ceil(Math.max(totalCount - 1, 0) / PAGE_SIZE);
      if (currentPage > 0 && currentPage >= nextPageCount) {
        setCurrentPage(Math.max(nextPageCount - 1, 0));
      } else {
        loadUsers();
      }
    } catch (error) {
      onNotify(error.message || "Failed to delete user.", "failed");
    } finally {
      setIsDeletingUser(false);
    }
  };

  const isCreateDisabled =
    isCreatingUser ||
    !userForm.firstName.trim() ||
    !userForm.lastName.trim() ||
    !isValidEmail(userForm.email);

  return (
    <div
      className="rounded-2xl border border-[#E6E8EC] bg-white dark:bg-darkThemeModalBg dark:border-white/10 shadow-appShadow overflow-hidden"
      data-testid="customer-users-panel"
    >
      <div className="border-b border-[#E6E8EC] dark:border-white/10 px-5 py-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-base font-semibold text-dark-grey dark:text-white">Users</h3>
          <p className="text-sm text-dark-grey/70 dark:text-white/60 mt-1">
            {totalCount} total
          </p>
        </div>
        <Button
          className="flex items-center justify-center gap-2 bg-theme text-white px-4 py-2 rounded-md text-sm font-semibold hover:bg-theme/90 transition w-full sm:w-auto"
          onClick={() => setIsAddOpen(true)}
          data-testid="add-customer-user-button"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M12 5V19" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
            <path d="M5 12H19" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          Add User
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center min-h-48">
          <LoadingSpinner />
        </div>
      ) : users.length === 0 ? (
        <div className="px-5 py-12 text-center" data-testid="customer-users-empty">
          <p className="text-base font-semibold text-dark-grey dark:text-white">No users yet</p>
          <p className="mt-2 text-sm text-dark-grey/70 dark:text-white/60">
            Add the first user so this customer can sign in.
          </p>
        </div>
      ) : (
        <div className="divide-y divide-[#E6E8EC] dark:divide-white/10">
          {users.map((user) => (
            <div
              key={user.unid}
              className="px-5 py-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
              data-testid={`customer-user-row-${user.unid}`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="min-w-0 max-w-md truncate text-sm font-semibold text-dark-grey dark:text-white"
                    title={getUserDisplayName(user)}
                  >
                    {getUserDisplayName(user)}
                  </span>
                  <span className="inline-flex rounded-full bg-[#F1F2F4] dark:bg-white/10 px-3 py-1 text-xs font-semibold text-[#5F6368] dark:text-white/70">
                    {accessTypeLabels[user.userAccessType] || user.userAccessType}
                  </span>
                </div>
                <p className="mt-1 truncate text-sm text-dark-grey/70 dark:text-white/60" title={user.email}>
                  {user.email}
                </p>
              </div>
              {isSameEmail(user.email, primaryContact) ? (
                <span
                  className="text-xs font-semibold uppercase tracking-[0.08em] text-dark-grey/50 dark:text-white/40 shrink-0"
                  data-testid={`primary-contact-user-${user.unid}`}
                >
                  Primary contact
                </span>
              ) : (
                <Button
                  className="px-4 py-2 rounded-md border border-[#EF4444] text-[#EF4444] text-sm font-semibold hover:bg-[#EF4444] hover:text-white transition w-full sm:w-auto"
                  onClick={() => setUserToDelete(user)}
                  data-testid={`delete-customer-user-${user.unid}`}
                >
                  Delete
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      {pageCount > 1 && (
        <div className="px-5 pb-5">
          <Pagination
            currentPage={currentPage}
            pageCount={pageCount}
            onPageChange={({ selected }) => setCurrentPage(selected)}
          />
        </div>
      )}

      {isAddOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div
            className="w-full max-w-md rounded-2xl bg-white dark:bg-darkThemeModalBg shadow-appShadow"
            data-testid="add-customer-user-modal"
          >
            <div className="flex items-center justify-between border-b border-[#E6E8EC] dark:border-white/10 px-6 py-4">
              <h3 className="text-lg font-semibold text-dark-grey dark:text-white">Add User</h3>
              <Button
                className="text-dark-grey/70 dark:text-white/70 hover:text-dark-grey dark:hover:text-white"
                onClick={closeAddModal}
                aria-label="Close"
                data-testid="add-customer-user-close"
              >
                ✕
              </Button>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div className="space-y-2">
                <Label className="text-sm text-dark-grey dark:text-white" htmlFor="customer-user-first-name">
                  First Name
                </Label>
                <Input
                  id="customer-user-first-name"
                  value={userForm.firstName}
                  onChange={(event) => {
                    const { value } = event.target;
                    setUserForm((current) => ({ ...current, firstName: value }));
                  }}
                  placeholder="First Name"
                  className={fieldClassName}
                  data-testid="customer-user-first-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm text-dark-grey dark:text-white" htmlFor="customer-user-last-name">
                  Last Name
                </Label>
                <Input
                  id="customer-user-last-name"
                  value={userForm.lastName}
                  onChange={(event) => {
                    const { value } = event.target;
                    setUserForm((current) => ({ ...current, lastName: value }));
                  }}
                  placeholder="Last Name"
                  className={fieldClassName}
                  data-testid="customer-user-last-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm text-dark-grey dark:text-white" htmlFor="customer-user-email">
                  Email Address
                </Label>
                <Input
                  id="customer-user-email"
                  type="email"
                  value={userForm.email}
                  onChange={(event) => {
                    const { value } = event.target;
                    setUserForm((current) => ({ ...current, email: value }));
                  }}
                  placeholder="john@example.com"
                  className={fieldClassName}
                  data-testid="customer-user-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm text-dark-grey dark:text-white" htmlFor="customer-user-password">
                  Password
                </Label>
                <Input
                  id="customer-user-password"
                  type="password"
                  value={userForm.password}
                  onChange={(event) => {
                    const { value } = event.target;
                    setUserForm((current) => ({ ...current, password: value }));
                  }}
                  placeholder="Leave blank to email a password reset link"
                  className={fieldClassName}
                  data-testid="customer-user-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label
                  className="text-sm text-dark-grey dark:text-white"
                  htmlFor="customer-user-access-type"
                >
                  Access Type
                </Label>
                <AccessTypeDropdown
                  id="customer-user-access-type"
                  value={userForm.userAccessType}
                  onSelect={(userAccessType) => {
                    setUserForm((current) => ({ ...current, userAccessType }));
                  }}
                  data-testid="customer-user-access-type"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-[#E6E8EC] dark:border-white/10 px-6 py-4">
              <Button
                className="bg-white text-dark-grey border border-[#E6E8EC] dark:border-theme dark:text-white dark:bg-transparent px-4 py-2 rounded-md text-sm font-semibold hover:bg-theme hover:text-white transition-all duration-300 ease-in-out"
                onClick={closeAddModal}
                data-testid="add-customer-user-cancel"
              >
                Cancel
              </Button>
              <Button
                className="bg-theme text-white px-4 py-2 rounded-md text-sm font-semibold hover:bg-theme/90 transition disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleCreateUser}
                disabled={isCreateDisabled}
                data-testid="add-customer-user-submit"
              >
                {isCreatingUser ? "Adding..." : "Add User"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {userToDelete && (
        <ConfirmModal
          title="Delete User"
          subText="This action cannot be undone."
          confirmMessage={`${getUserDisplayName(userToDelete)} (${userToDelete.email}) will be permanently deleted.`}
          cancelText="Cancel"
          submitText={isDeletingUser ? "Deleting..." : "Delete User"}
          disableCancel={isDeletingUser}
          disableSubmit={isDeletingUser}
          onCancel={() => {
            if (isDeletingUser) return;
            setUserToDelete(null);
          }}
          onSubmit={handleDeleteUserConfirmed}
        />
      )}
    </div>
  );
};

export default CustomerUsersPanel;
